import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  Link
} from '@mui/material';
import AuthService from '../services/AuthService';
import LoadingIndicator from '../components/LoadingIndicator';
import toastService from '../utils/toastService';
import { PrimaryButton } from '../components';

const Profile: React.FC = () => {
  const { user, updateUserInfo } = useAuth();
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
    }
  }, [user]);

  const handleNameUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await AuthService.updateUserName(name);

      if (user) {
        updateUserInfo({
          ...user,
          name: name
        });
      }

      toastService.success('Nome atualizado com sucesso!');
    } catch (err: any) {
      toastService.error(err.response?.data?.message || 'Falha ao atualizar o nome. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toastService.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    try {
      await AuthService.updateUserPassword(
        currentPassword,
        newPassword
      );

      toastService.success('Senha atualizada com sucesso!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toastService.error(err.response?.data?.message || 'Falha ao atualizar a senha. Verifique se a senha atual está correta.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingIndicator message="Atualizando informações..." />;
  }

  return (
    <Container maxWidth="md" sx={{ my: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Meu Perfil
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleNameUpdate} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Informações Pessoais
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                value={user?.email}
                margin="normal"
                disabled
                helperText="O email não pode ser alterado"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PrimaryButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!name || name === user?.name}
              >
                Atualizar Nome
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handlePasswordUpdate}>
          <Typography variant="h6" gutterBottom>
            Alterar Senha
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Senha Atual"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Nova Senha"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                type="password"
                label="Confirmar Nova Senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={newPassword !== confirmPassword && confirmPassword !== ''}
                helperText={newPassword !== confirmPassword && confirmPassword !== '' ? "As senhas não coincidem" : ""}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PrimaryButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={!currentPassword || !newPassword || !confirmPassword}
              >
                Atualizar Senha
              </PrimaryButton>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
